import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { xcg, cwPrettyDate, humanArrival, humanCheckedInAt, nightsBetween } from '../lib/format';
import { StatusBadge } from './StatusBadge';
import { X } from 'lucide-react';

export function BookingDetailDrawer({ bookingNumber, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!bookingNumber) return;
    setData(null);
    setErr('');
    api.get(`/bookings/${bookingNumber}`).then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || 'Could not load booking'); });
    return () => { cancelled = true; };
  }, [bookingNumber]);

  if (!bookingNumber) return null;
  const b = data?.booking;
  const audit = data?.audit || [];

  return (
    <div className="fixed inset-0 z-50" data-testid="booking-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[560px] bg-[#FBFCFB] border-l border-[#CFD8CF] overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 bg-[#FBFCFB] border-b border-[#CFD8CF] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#333]/60">Booking</div>
            <div className="font-heading text-xl text-[#1F5A24]">{bookingNumber}</div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="p-2 rounded-lg hover:bg-[#E8F5E9]"><X size={20} /></button>
        </div>

        {err && <div className="p-5 text-[#B26A00]">{err}</div>}
        {!data && !err && <div className="p-5 text-[#333]/60">Loading…</div>}
        {b && (
          <div className="p-5 space-y-6">
            <div>
              <div className="text-2xl font-heading font-medium">{b['Name']}</div>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={b['Status']} checkedIn={!!b['CheckedInAt'] && b['Status'] === 'Confirmed'} />
                <StatusBadge status={b['PaymentStatus']} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Email" v={b['Email']} />
              <Field label="Phone" v={b['Phone']} />
              <Field label="Check in" v={`${cwPrettyDate(b['CheckIn'])} — ${humanArrival(b['CheckIn'])}`} />
              <Field label="Check out" v={cwPrettyDate(b['CheckOut'])} />
              <Field label="Room" v={b['RoomType']} />
              <Field label="Guests" v={String(b['Guests'])} />
              <Field label="Nights" v={String(nightsBetween(b['CheckIn'], b['CheckOut']))} />
              <Field label="Amount" v={xcg(b['Amount'])} />
              <Field label="Payment method" v={b['PaymentMethod']} />
              <Field label="Reminders sent" v={`${b['ReminderCount'] ?? 0} of 3`} />
              {b['CheckedInAt'] && <Field label="Arrived" v={`${humanCheckedInAt(b['CheckedInAt'])}${b['CheckedInBy'] ? ` — by ${b['CheckedInBy']}` : ''}`} />}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#333]/60 mb-2">QR code</div>
                <img alt="QR code" src={b['QRURL']} className="w-full max-w-[240px] rounded-lg border border-[#CFD8CF] bg-white p-2" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#333]/60 mb-2">Payment proof</div>
                <img alt="Payment proof" src={b['ProofURL']} className="w-full rounded-lg border border-[#CFD8CF] object-cover" />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-[#333]/60 mb-2">Audit history</div>
              {audit.length === 0 ? (
                <div className="text-sm text-[#333]/60">No history yet</div>
              ) : (
                <ul className="space-y-2">
                  {audit.map((a, i) => (
                    <li key={i} className="text-sm border border-[#CFD8CF] rounded-lg p-3 bg-white">
                      <div className="flex justify-between">
                        <span className="font-medium">{a.action.replace('_', ' ')}</span>
                        <span className="text-[#333]/60">{new Date(a.at).toLocaleString('en-GB')}</span>
                      </div>
                      <div className="text-[#333]/80">by {a.user_name || a.user_email}</div>
                      {a.old_value && <div>from <em>{a.old_value}</em> to <em>{a.new_value}</em></div>}
                      {a.reason && <div className="text-[#B26A00] mt-1">Reason: {a.reason}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, v }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#333]/60">{label}</div>
      <div className="mt-0.5 text-[#333]">{v}</div>
    </div>
  );
}
